import { useState } from 'react';
import { Button } from '@/components/ui/button';
import MemberSelectTable from 'components/IncidentConfig/MemberSelectTable';
import { useOptionalCreateRosterDraft } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import {
  useAddUsersToIncidentRoster,
  useGetRostersForGroup,
  useSendSeatActivationRequests,
  useUpdatePositionAssignment,
} from 'hooks/incident/useRoster';
import { useHwcgFlag } from 'utils/featureFlags';

import { SeatActivationRequestControl } from './SeatActivationRequestControl';

/** `secondary` stays readable on old seats; the modal no longer offers it. */
export type PositionDesignation = 'primary' | 'secondary';

type Slot = {
  designation: PositionDesignation;
  label: string;
};

const SLOTS: Slot[] = [{ designation: 'primary', label: 'PRIMARY' }];

function designationOnPosition(
  member: RosterEntry,
  positionId: string
): string | null {
  return (
    member.incidentPositions?.find((seat) => seat.positionId === positionId)
      ?.designation ?? null
  );
}

/** Everyone but the primary, so people designated secondary before that slot was removed still show as assignees. */
export function membersWithoutLeaderSlots(
  members: RosterEntry[],
  positionId: string
): RosterEntry[] {
  return members.filter(
    (member) => designationOnPosition(member, positionId) !== 'primary'
  );
}

type PositionLeaderSlotsProps = {
  incidentId: string;
  groupId: string;
  positionId: string;
  positionName: string;
  members: RosterEntry[];
  rostersForGroupQuery: ReturnType<typeof useGetRostersForGroup>;
  isDraft: boolean;
  /**
   * The planned period whose roster the slots show; omitted for the current
   * period.
   */
  plannedPeriodId?: string | null;
};

export function PositionLeaderSlots({
  incidentId,
  groupId,
  positionId,
  positionName,
  members,
  rostersForGroupQuery,
  isDraft,
  plannedPeriodId,
}: PositionLeaderSlotsProps) {
  const [picking, setPicking] = useState<PositionDesignation | null>(null);
  const addUsers = useAddUsersToIncidentRoster(incidentId, groupId);
  const updateAssignment = useUpdatePositionAssignment();
  const sendRequests = useSendSeatActivationRequests(incidentId, groupId);
  const isCreateMode = !!useOptionalCreateRosterDraft();
  const requestSeparately = useHwcgFlag() && !isDraft && !isCreateMode;

  const occupant = (designation: PositionDesignation) =>
    members.find(
      (member) => designationOnPosition(member, positionId) === designation
    );

  const assign = (
    designation: PositionDesignation,
    userGuid: string,
    sendActivationRequest = false
  ) => {
    const current = occupant(designation);
    if (current?.userId === userGuid) {
      if (sendActivationRequest) {
        sendRequests.mutate({
          plannedPeriodId,
          seats: [{ userGuid, positionId }],
        });
      }
      setPicking(null);
      return;
    }
    if (current) {
      updateAssignment.mutate({
        member: { user_guid: current.userId },
        incidentMemberId: current.incidentMemberId,
        positionId,
        designation: null,
        isDraft,
        plannedPeriodId,
      });
    }
    const alreadyOnSeat = members.find((member) => member.userId === userGuid);
    if (alreadyOnSeat) {
      updateAssignment.mutate({
        member: { user_guid: alreadyOnSeat.userId },
        incidentMemberId: alreadyOnSeat.incidentMemberId,
        positionId,
        designation,
        isDraft,
        plannedPeriodId,
      });
      if (sendActivationRequest) {
        sendRequests.mutate({
          plannedPeriodId,
          seats: [{ userGuid, positionId }],
        });
      }
    } else {
      addUsers.mutate({
        positionAssignments: [
          {
            positionId,
            positionName,
            incidentMemberUserId: userGuid,
            isLeader: designation === 'primary',
            designation,
          },
        ],
        isDraft,
        plannedPeriodId,
        sendActivationRequest,
      });
    }
    setPicking(null);
  };

  const clearSlot = (designation: PositionDesignation) => {
    const current = occupant(designation);
    if (!current) return;
    updateAssignment.mutate({
      member: { user_guid: current.userId },
      incidentMemberId: current.incidentMemberId,
      positionId,
      designation: null,
      isDraft,
      plannedPeriodId,
    });
  };

  return (
    <div className="tw:space-y-3">
      <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase">
        {isCreateMode
          ? 'On roster when the incident is created'
          : plannedPeriodId
            ? "On this period's roster"
            : 'On roster now'}
      </div>
      <div className="tw:grid tw:gap-3">
        {SLOTS.map((slot) => {
          const member = occupant(slot.designation);
          return (
            <div
              key={slot.designation}
              className="tw:rounded-md tw:border tw:border-[#2a4568] tw:bg-[#1a2f4d] tw:p-3 tw:text-white"
            >
              <div className="tw:text-[11px] tw:font-semibold tw:tracking-wide tw:text-white/70 tw:uppercase">
                {positionName} - {slot.label}
              </div>
              {member ? (
                <>
                  <div className="tw:mt-2 tw:text-sm tw:font-medium tw:text-white">
                    {member.userName || member.emailAddress || 'Assigned'}
                  </div>
                  <div className="tw:mt-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="tw:border-white/40 tw:bg-transparent tw:text-white tw:hover:bg-white/10 tw:hover:text-white"
                      onClick={() => setPicking(slot.designation)}
                    >
                      Change
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="tw:text-white/80 tw:hover:bg-white/10 tw:hover:text-white"
                      onClick={() => clearSlot(slot.designation)}
                    >
                      Clear
                    </Button>
                    {requestSeparately ? (
                      <SeatActivationRequestControl
                        incidentId={incidentId}
                        groupId={groupId}
                        positionId={positionId}
                        member={member}
                        plannedPeriodId={plannedPeriodId}
                      />
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  <div className="tw:mt-2 tw:text-sm tw:font-medium tw:text-white">
                    Unassigned
                  </div>
                  <div className="tw:mt-1 tw:text-xs tw:text-red-300">Gap</div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="tw:mt-3 tw:border-white/40 tw:bg-transparent tw:text-white tw:hover:bg-white/10 tw:hover:text-white"
                    onClick={() => setPicking(slot.designation)}
                  >
                    Assign
                  </Button>
                </>
              )}
            </div>
          );
        })}
      </div>
      {picking ? (
        <MemberSelectTable
          groupRoster={rostersForGroupQuery}
          currentPositionMembers={members}
          selectingLeader={picking === 'primary'}
          onSelectMembers={(selected) => {
            const [first] = selected;
            if (first) assign(picking, first.user_guid);
            else setPicking(null);
          }}
          onSelectMembersAndRequest={
            requestSeparately
              ? (selected) => {
                  const [first] = selected;
                  if (first) assign(picking, first.user_guid, true);
                  else setPicking(null);
                }
              : undefined
          }
          closeTable={() => setPicking(null)}
        />
      ) : null}
    </div>
  );
}
